import {
  AccessLevel,
  Box,
  Button,
  Checkbox,
  Flex,
  generatePublishTitle,
  H4,
  Spinner,
} from '@ouster/webviz-react-components';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AccessLevelSelector } from './AccessLevelSelector';
import { LimitedAccessConfig } from './LimitedAccessConfig';
import { PublicAccessConfig } from './PublicAccessConfig';
import useChakraHook from '../../hooks/useChakraHook';
import { useStoreActions, useStoreState } from '../../reducers';
import {
  AccessStatus,
  MappedOrg,
  RecordingAccessInfo,
  RecordingPublicityAccessConfig,
} from '../../types';

/* 
  When the recording has a publication_uuid, it must has been published
  Then we need to check its access status.
  access_level = NONE: not a sample recording
  access_level = PUBLIC_SAMPLE: a public-public sample recording, but not featured
  access_level = PUBLIC_SAMPLE_FEATURED: a public-public sample recording, and featured. need thumbnail and title
  access_level = PRIVATE_SAMPLE: a public-private sample recording, only logged in users can access
*/

interface RecordingAccessConfigProps {
  recording: RecordingPublicityAccessConfig;
  onClose: () => void;
}

export default function RecordingAccessConfig({
  recording,
  onClose,
}: RecordingAccessConfigProps) {
  const { fetchFullOrgList } = useStoreActions(
    (actions) => actions.organization
  );
  const {
    loadPrivateSharedToOrgs,
    savePrivateSharedToOrgs,
    setPrivateSharedToOrgs,
    loadAccessLevel,
    updateAccessLevel,
    publishDrive,
    deletePrivateSharedToOrgs,
    unpublishDriveById,
  } = useStoreActions((actions) => actions.drive);
  const { fullOrgList } = useStoreState(
    (storeState) => storeState.organization
  );
  const { privateSharedToOrgs } = useStoreState(
    (storeState) => storeState.drive
  );

  const { showToast } = useChakraHook();

  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState<MappedOrg[]>([]);

  // Map organizations to the CreatableMultiSelect options.
  const mappedOrgs = useMemo<MappedOrg[]>(
    () =>
      fullOrgList
        // API change so we have orgs with different statuses. Only show ACTIVE orgs in the sharing options
        .filter((org) => org.status === 'ACTIVE')
        .map((org) => ({
          label: `${org.org_name} (${org.creator_email})`,
          value: org.org_id.toString(),
          data: org,
        })),
    [fullOrgList]
  );

  const [selectedStatus, setSelectedStatus] = useState<AccessStatus>('PRIVATE');
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('');
  const [title, setTitle] = useState<string>(recording.recording_title || '');
  const [confirmUnpublish, setConfirmUnpublish] = useState<boolean>(false);

  const originalAccessInfo = useRef<RecordingAccessInfo>();

  const loadingOrgs = useCallback(async () => {
    try {
      const hasPartner = await loadPrivateSharedToOrgs({
        orgId: recording.organization_id,
        driveId: Number(recording.id),
      });

      if (hasPartner) {
        setSelectedStatus('PARTNER_PREVIEW');
      } else {
        setSelectedStatus('PRIVATE');
      }
    } catch (error) {
      console.error('Error loading private shared to orgs:', error);
    }
  }, [loadPrivateSharedToOrgs, recording.organization_id, recording.id]);

  const requiresUnpublishConfirmation = useMemo(
    () =>
      // Show checkbox if recording is published and user selects PRIVATE or PARTNER_PREVIEW
      recording.publication_uuid &&
      (selectedStatus === 'PRIVATE' || selectedStatus === 'PARTNER_PREVIEW'),
    [recording.publication_uuid, selectedStatus]
  );

  useEffect(() => {
    if (fullOrgList.length === 0 && selectedStatus === 'PARTNER_PREVIEW') {
      fetchFullOrgList();
    }
  }, [fetchFullOrgList, fullOrgList.length, selectedStatus]);

  useEffect(() => {
    if (privateSharedToOrgs.length > 0 && mappedOrgs.length > 0) {
      const selected = mappedOrgs.filter((mappedOrg) =>
        privateSharedToOrgs.includes(mappedOrg.data.org_id)
      );
      setSelectedOrgs(selected);
    }
  }, [mappedOrgs, privateSharedToOrgs]);

  const initializeAccessLevel = useCallback(async () => {
    setIsInitializing(true);
    try {
      if (recording.publication_uuid) {
        const result = await loadAccessLevel({
          organizationId: recording.organization_id,
          driveUuid: recording.publication_uuid,
        });

        originalAccessInfo.current = result;

        switch (result.access_level) {
          case 'PUBLIC_SAMPLE':
            setSelectedStatus('PUBLIC_PUBLIC_NON_FEATURED');
            break;
          case 'PUBLIC_SAMPLE_FEATURED':
            setSelectedStatus('PUBLIC_PUBLIC_FEATURED');
            setThumbnailUrl(result?.thumbnail || '');
            setTitle(result?.title || '');
            break;
          case 'PRIVATE_SAMPLE':
            setSelectedStatus('PRIVATE_PUBLIC');
            break;
          default:
            // WHEN the recording is published, but not any kinds of sample data, it can be PRIVATE
            // or shared with specific partners, we need to load the private shared orgs to decide the status
            await loadingOrgs();
            break;
        }
      } else {
        await loadingOrgs();
      }
    } catch (error) {
      console.error('Error initializing access level:', error);
      setSelectedStatus('PRIVATE');
    } finally {
      setIsInitializing(false);
    }
  }, [
    recording.organization_id,
    recording.publication_uuid,
    loadAccessLevel,
    loadingOrgs,
  ]);

  useEffect(() => {
    initializeAccessLevel();
  }, [initializeAccessLevel]);

  const renderPublicConfig = () => (
    <PublicAccessConfig
      thumbnailUrl={thumbnailUrl}
      setThumbnailUrl={setThumbnailUrl}
      title={title}
      setTitle={setTitle}
    />
  );

  const renderLimitedConfig = () => (
    <LimitedAccessConfig
      selectedOrgs={selectedOrgs}
      setSelectedOrgs={setSelectedOrgs}
      mappedOrgs={mappedOrgs}
    />
  );

  const renderUnpublishConfirmation = () => {
    if (!requiresUnpublishConfirmation) return null;

    return (
      <Box mt={4} p={4} border="1px solid" borderColor="gray.500" rounded={4}>
        <Checkbox
          isChecked={confirmUnpublish}
          onChange={(e) => setConfirmUnpublish(e.target.checked)}
          label="I want to unpublish this recording, so it is no longer accessible publicly."
        />
      </Box>
    );
  };

  useEffect(
    () =>
      // Cleanup function that runs when component unmounts
      () => {
        setSelectedOrgs([]);
        setPrivateSharedToOrgs([]);
      },
    [setPrivateSharedToOrgs]
  );

  /* 
    When the access level is public public (featured or not) or private public, the recording need to be published first. Hence, this function will ensure the recording is published and return the publication uuid.
  */
  const ensurePublished = async () => {
    if (recording.publication_uuid) {
      return recording.publication_uuid;
    }

    const publicationInfo = await publishDrive({
      title: generatePublishTitle(recording),
      orgId: recording.organization_id,
      driveId: Number(recording.id),
    });

    return publicationInfo.uuid;
  };

  /* 
    If the recoding used to be shared with specific orgs, and when it is set to private or any types of public, we need to delete the shared info first.
  */
  const cleanupPrivateSharedOrgs = async () => {
    if (privateSharedToOrgs.length > 0) {
      await deletePrivateSharedToOrgs({
        organizationId: recording.organization_id,
        driveId: Number(recording.id),
      });
    }
  };

  const updateRecordingAccessLevel = async (
    driveUuid: string,
    accessLevel: AccessLevel
  ) => {
    const payload: {
      organizationId: number;
      driveUuid: string;
      accessLevel: AccessLevel;
      thumbnailUrl?: string;
      title?: string;
    } = {
      organizationId: recording.organization_id,
      driveUuid,
      accessLevel,
    };

    if (accessLevel === AccessLevel.PUBLIC_SAMPLE_FEATURED) {
      payload.thumbnailUrl = thumbnailUrl;
      payload.title = title;
    }

    await updateAccessLevel(payload);
  };

  const handlePublicAccess = async () => {
    // Clean up any existing private shared orgs if existed
    await cleanupPrivateSharedOrgs();

    // Ensure the recording is published
    const driveUuid = await ensurePublished();

    // Update access level to public
    await updateRecordingAccessLevel(
      driveUuid,
      selectedStatus === 'PUBLIC_PUBLIC_FEATURED'
        ? AccessLevel.PUBLIC_SAMPLE_FEATURED
        : AccessLevel.PUBLIC_SAMPLE
    );
  };

  const handleLimitedAccess = async () => {
    if (selectedStatus === 'PARTNER_PREVIEW') {
      // Handle specific org sharing
      if (
        recording.publication_uuid &&
        originalAccessInfo.current?.access_level !== 'NONE'
      ) {
        // make sure we remove recording from the sample list (private or public)
        await updateRecordingAccessLevel(
          recording.publication_uuid,
          AccessLevel.NONE
        );
      }

      if (requiresUnpublishConfirmation && confirmUnpublish) {
        // user choose to unpublish the recording as well
        await unpublishDriveById({
          orgId: 1,
          driveId: Number(recording.id),
        });
      }

      // Save private shared orgs
      await savePrivateSharedToOrgs({
        organizationId: recording.organization_id,
        driveId: Number(recording.id),
        sharedToOrgs: selectedOrgs.map((org) => org.data.org_id),
      });
    } else {
      // When shared as private public. We need to make sure
      // - it is not shared with specific orgs
      // - it is published with public access
      // - it has access level as PRIVATE_SAMPLE

      // Clean up private shared orgs
      await cleanupPrivateSharedOrgs();

      let driveUuid = recording.publication_uuid;
      // To share as private public sample, the recording must be published first
      if (!driveUuid) {
        // Ensure the recording is published
        driveUuid = await ensurePublished();
      }
      await updateRecordingAccessLevel(driveUuid, AccessLevel.PRIVATE_SAMPLE);
    }
  };

  const handlePrivateAccess = async () => {
    // Update to NONE if published
    if (recording.publication_uuid) {
      await updateRecordingAccessLevel(
        recording.publication_uuid,
        AccessLevel.NONE
      );
    }

    if (requiresUnpublishConfirmation && confirmUnpublish) {
      // user choose to unpublish the recording as well
      await unpublishDriveById({
        orgId: 1,
        driveId: Number(recording.id),
      });
    }

    // Clean up private shared orgs
    await cleanupPrivateSharedOrgs();
  };

  const saveConfig = async () => {
    localStorage.setItem('recordingAccessConfigChanged', 'true');
    setIsSaving(true);
    try {
      switch (selectedStatus) {
        case 'PUBLIC_PUBLIC_NON_FEATURED':
        case 'PUBLIC_PUBLIC_FEATURED':
          await handlePublicAccess();
          break;
        case 'PRIVATE_PUBLIC':
        case 'PARTNER_PREVIEW':
          await handleLimitedAccess();
          break;
        case 'PRIVATE':
          await handlePrivateAccess();
          break;
        default:
          onClose();
          return;
      }
      onClose();
    } catch (error) {
      console.error('Error saving config:', error);
      showToast({
        title: 'Failed to config recording access',
        description: `${error}`,
        status: 'error',
      });
      localStorage.removeItem('recordingAccessConfigChanged');
    } finally {
      setIsSaving(false);
    }
  };

  const canSubmit = useMemo(() => {
    if (isSaving) return false;
    if (selectedStatus === 'PUBLIC_PUBLIC_FEATURED') {
      return !!(thumbnailUrl && title);
    }
    if (selectedStatus === 'PARTNER_PREVIEW') {
      return selectedOrgs.length > 0;
    }
    return true;
  }, [isSaving, selectedStatus, thumbnailUrl, title, selectedOrgs.length]);

  if (isInitializing) {
    return (
      <Box
        width={800}
        display="grid"
        rowGap={8}
        justifyContent="center"
        alignItems="center"
      >
        <Box display="flex" justifyContent="center" alignItems="center" gap={4}>
          <Spinner /> Loading configure
        </Box>
      </Box>
    );
  }

  return (
    <Box
      width="860px"
      display="grid"
      rowGap={8}
      data-testid="recording-access-config-root"
    >
      <Box
        display="grid"
        rowGap={4}
        p={6}
        rounded={4}
        bg="whiteAlpha.100"
        border={1}
        borderColor="whiteAlpha.200"
        borderStyle="solid"
        maxH="55vh"
        overflowY="auto"
      >
        <Box>
          <H4 fontSize={15}>Access Level</H4>
          <Box as="p" color="gray.300" mt={1} fontSize={14}>
            Configure who can access this recording.
          </Box>
        </Box>

        <AccessLevelSelector
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          renderPublicConfig={renderPublicConfig}
          renderLimitedConfig={renderLimitedConfig}
          renderUnpublishConfirmation={renderUnpublishConfirmation}
          data-testid="recording-access-config-access-level-selector"
        />
      </Box>

      <Flex placeContent="space-between" alignItems="center">
        <Button
          minWidth={150}
          justifySelf="start"
          mt={4}
          onClick={onClose}
          variant="outline"
          colorScheme="gray"
          disabled={isSaving}
          data-testid="recording-access-config-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          minWidth={150}
          justifySelf="end"
          mt={4}
          onClick={saveConfig}
          disabled={!canSubmit}
          data-testid="recording-access-config-submit-btn"
        >
          {isSaving ? 'Saving...' : 'Submit'}
        </Button>
      </Flex>
    </Box>
  );
}
